import Storage from "Helpers/Storage";
import env from "react-dotenv";
import AuthHelper from "Helpers/AuthHelper";
import axios from "axios";

// Auth ENTRY endpoints handle their own 401s (wrong password, refresh failure)
// and must not trigger the session-nuke redirect. Note that /auth/me is a
// session-validity check and is intentionally NOT here: a 401 from it means a
// dead session and must clear + redirect like any other endpoint.
const AUTH_ENTRY_ENDPOINTS = [
    '/auth/signin',
    '/auth/signup',
    '/auth/refresh',
    '/auth/recover',
    '/auth/redefine',
];

// Once a redirect (dead session / inactive subscription) is initiated we flip
// this so sibling in-flight requests short-circuit instead of running against
// cleared credentials and firing setData(undefined)/toasts before navigation.
let isRedirecting = false;

const redirectTo = (path) => {
    isRedirecting = true;
    window.location.href = path;
};

const Client = {
    _request: async (endPoint, method, body = {}, hasFile = false, skipAuthRefresh = false) => {
        // A redirect is already underway: don't start new work.
        if (isRedirecting) {
            return { status: 0, json: {} };
        }

        let formData = {};
        if (hasFile) {
            formData = new FormData();
            Object.keys(body).forEach(key => formData.append(key, body[key]));
        }

        let data = {
            method: method,
            headers: hasFile ? {
                "Content-Type": 'multipart/form-data',
                "Access-Control-Allow-Origin": '*'
            } : {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": '*'
            },
            data: method != 'GET' ? (hasFile ? formData : JSON.stringify(body)) : null
        };
        if (Storage.exists('token')) {
            // Refresh requests must not re-enter the refresh flow (see AuthHelper mutex).
            let valid = skipAuthRefresh ? true : await AuthHelper.checkForTokenRefresh();

            if (!valid) {
                Storage.clear();
            }

            if (!valid && window.location.pathname.indexOf('members') !== -1) {
                if (!isRedirecting) {
                    redirectTo("/auth");
                }
                return { status: 401, json: {} };
            }

            if (valid) {
                data.headers.Authorization = AuthHelper.getBearerToken();
            }
        }

        let url = ['localhost', '127.0.0.1'].indexOf(window.location.hostname) != -1 ? env.API_URL_DEV : env.API_URL_PROD;

        try {
            let req = await axios({
                url: `${url}${endPoint}`,
                ...data
            });

            return {
                status: req.status,
                json: req.status !== 204 ? await req.data : null
            };
        } catch (error) {
            const status = error.response?.status;
            const isAuthEntryEndpoint = AUTH_ENTRY_ENDPOINTS.some((p) => endPoint.indexOf(p) !== -1);

            // Expired/invalid session: drop credentials and force a fresh login.
            // Auth ENTRY endpoints (signin, refresh, ...) handle their own 401s;
            // /auth/me and every other endpoint fall through to the nuke.
            if (status === 401 && !isAuthEntryEndpoint) {
                Storage.clear();
                if (!isRedirecting && window.location.pathname !== '/auth') {
                    redirectTo("/auth");
                }
                return { status: 401, json: error.response?.data ?? {} };
            }

            // Active session but the subscription is no longer valid: send the user
            // to their account page instead of logging them out. A plain 403
            // (e.g. non-admin hitting an admin route) is left to surface below.
            if (status === 403 && error.response?.data?.error === 'subscription_inactive') {
                if (!isRedirecting && window.location.pathname !== '/members/account') {
                    redirectTo("/members/account");
                }
                return { status: 403, json: error.response?.data ?? {} };
            }

            throw error;
        }
    },
    get: async (endPoint) => {
        return await Client._request(endPoint, 'GET');
    },

    post: async (endPoint, body = {}, skipAuthRefresh = false) => {
        return await Client._request(endPoint, 'POST', body, false, skipAuthRefresh);
    },

    postFile: async (endPoint, body = {}) => {
        return await Client._request(endPoint, 'POST', body, true);
    },

    put: async (endPoint, body) => {
        return await Client._request(endPoint, 'PUT', body);
    },

    destroy: async (endPoint) => { // Unable to name as delete
        return await Client._request(endPoint, 'DELETE');
    }
};

export default Client;
